import { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  MapPinned,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import { useAors, useDeleteAor, useUpsertAor } from 'hooks/useAors';
import type { AreaOfResponsibility } from 'types/aor';
import { useAorText } from 'utils/aorTerminology';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { validateGeoLocationSize } from 'utils/validateGeoLocationSize';
import { AorEditorModal } from './AorEditorModal';
import { fileLabelOf, parseGeoJsonAors } from './geoJsonAors';

function plainText(html: string | null | undefined): string {
  return (html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Lists the org's AORs with add, edit, delete, and GeoJSON upload. `compact`
 * drops the heading for embedding in the onboarding sheet.
 */
export function AorManager({
  compact = false,
  showAlertsWarning = false,
}: {
  compact?: boolean;
  /** Warn that AI Alerts need at least one AOR. */
  showAlertsWarning?: boolean;
}) {
  const aorText = useAorText();
  const { data: aors = [], isLoading } = useAors();
  const deleteAor = useDeleteAor();
  const upsertAor = useUpsertAor();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AreaOfResponsibility | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<AreaOfResponsibility | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parentNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );

  const openEditor = (aor: AreaOfResponsibility | null) => {
    setEditing(aor);
    setEditorOpen(true);
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);
    let created = 0;
    try {
      for (const file of files) {
        const label = fileLabelOf(file.name);
        let parsed;
        try {
          parsed = parseGeoJsonAors(await file.text(), label);
        } catch (error) {
          toast.error((error as Error).message);
          continue;
        }
        for (const entry of parsed.aors) {
          const { isValid, locationString } = validateGeoLocationSize(
            entry.geolocation
          );
          if (!isValid) continue;
          await upsertAor.mutateAsync({
            id: generateUUID(),
            group_guid: '',
            name: entry.name,
            description: entry.description,
            location: locationString,
            category_id: null,
            parent_item_id: null,
            cc_type: null,
          });
          created += 1;
        }
        if (parsed.skipped > 0) {
          toast.info(
            `${label}: skipped ${parsed.skipped} non-polygon feature${parsed.skipped === 1 ? '' : 's'}.`
          );
        }
        if (parsed.truncated > 0) {
          toast.warn(
            `${label}: only the first ${parsed.aors.length} polygons were added.`
          );
        }
      }
      if (created > 0) {
        toast.success(
          aorText(`Added ${created} AOR${created === 1 ? '' : 's'}.`)
        );
      }
    } catch {
      toast.error(aorText('Could not save the uploaded AORs.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="dark tw:flex tw:flex-col tw:gap-4 tw:text-foreground">
      {!compact ? (
        <div>
          <h2 className="tw:m-0 tw:text-base tw:font-semibold">
            {aorText('Areas of Responsibility')}
          </h2>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            {aorText(
              'AORs scope reports, alerts, and event automation rules to the places your organization covers.'
            )}
          </p>
        </div>
      ) : null}

      {showAlertsWarning && !isLoading && aors.length === 0 ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>
            {aorText(
              'At least one Area of Responsibility is required for AI Alerts.'
            )}
          </AlertTitle>
        </Alert>
      ) : null}

      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <Button type="button" onClick={() => openEditor(null)}>
          <Plus data-icon="inline-start" />
          {aorText('Add AOR')}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload data-icon="inline-start" />
          {uploading ? 'Uploading…' : 'Upload GeoJSON'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".geojson,.json,application/geo+json,application/json"
          multiple
          hidden
          aria-label="Upload GeoJSON"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (files.length) void uploadFiles(files);
          }}
        />
      </div>

      {!isLoading && aors.length === 0 ? (
        <Empty className="tw:border tw:border-dashed tw:border-border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MapPinned />
            </EmptyMedia>
            <EmptyTitle>{aorText('No AORs yet')}</EmptyTitle>
            <EmptyDescription>
              {aorText(
                'Draw an area, pick a FEMA region, or upload a GeoJSON file with one polygon per AOR.'
              )}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ItemGroup
          className={cn(
            'tw:gap-2',
            compact && 'tw:max-h-[50vh] tw:overflow-y-auto'
          )}
        >
          {aors.map((aor) => {
            const description = plainText(aor.description);
            const parentName = aor.parent_item_id
              ? parentNameById.get(aor.parent_item_id)
              : undefined;
            return (
              <Item key={aor.id} variant="outline" size="sm">
                <ItemContent className="tw:min-w-0">
                  <ItemTitle>{aor.name}</ItemTitle>
                  {parentName || description ? (
                    <ItemDescription className="tw:line-clamp-2">
                      {parentName ? `Under ${parentName}` : null}
                      {parentName && description ? ' · ' : null}
                      {description}
                    </ItemDescription>
                  ) : null}
                </ItemContent>
                <ItemActions>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Edit ${aor.name}`}
                    onClick={() => openEditor(aor)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${aor.name}`}
                    onClick={() => setPendingDelete(aor)}
                  >
                    <Trash2 />
                  </Button>
                </ItemActions>
              </Item>
            );
          })}
        </ItemGroup>
      )}

      <AorEditorModal
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditing(null);
        }}
        aor={editing}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {aorText(`Delete ${pendingDelete?.name ?? 'this AOR'}?`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {aorText(
                'Reports, alerts, and rules scoped to this Area of Responsibility stop covering it.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingDelete) deleteAor.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
